import { Procedure } from '../define-procedure';
import { Store } from '@ngxs/store';
import { RPCEvent } from '../define-event';
import { LabelsService } from 'src/app/story/labels.service';
import { getAnnotationEvents, getAnnotationProcedures } from './annotations';
import { AnnotationsIO } from '../models/annotations.io';
import { LabelsIO } from '../models/labels.io';
import { HotspotsIO } from '../models/hotspots.io';
import { getHotspotsProcedures, getHotspotsEvents } from './hotspots';
import { MessageEnum } from '../models/message.enum';
import { ToggleLabels } from 'src/app/engine/state/engine.actions';
import { HumanInterface } from '../../engine/models/human.interface';
import {
  AddLabel,
  RemoveLabel,
  SetAllLabelsShown,
  SetLabel,
  SetLabelScale,
  SetLabelsShown,
} from 'src/app/story/state/labels/story.labels.actions';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { MarkerModel } from 'src/app/story/models/marker.model';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { PickModel } from 'src/app/engine/models/pick.model';
import { FlyToObjectErrandModel } from 'src/app/story/models/errands/fly-to-object-errand.model';
import { FlyToChapterErrandModel } from 'src/app/story/models/errands/fly-to-chapter-errand.model';
import { uuid } from 'src/app/shared/uuid';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { EngineService } from 'src/app/engine/engine.service';
import { LabelStateModel } from 'src/app/story/models/labels/label-state.model';
import { FlyToCameraErrandModel } from 'src/app/story/models/errands/fly-to-camera-errand.model';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { trim } from 'lodash';
import { CameraModel } from 'src/app/engine/models/camera.model';

interface SetLabelEnabled {
  enable: boolean;
}

interface SetLabelScaleInterface {
  scale: number;
}

function getLabels(store: Store): LabelModel[] {
  return store.selectSnapshot(StoryLabelsState.getChapterLabels);
}
function showAllLabels(store: Store, shown: boolean) {
  store.dispatch(new SetAllLabelsShown(shown));
  return true;
}
function getLabelById(store: Store, labelId: string): LabelModel {
  const labels = getLabels(store);
  return labels.find((label) => labelId === label.id);
}
function getLabelsById(store: Store, labelIds: string[]): LabelModel[] {
  const labels = getLabels(store);
  return labels.filter((label) => labelIds.includes(label.id));
}

function storeLabel(store: Store, label: LabelModel, create: boolean): LabelModel {
  // validate we are attached to a valid object
  if (label && label.objectId) {
    store.dispatch(create ? new AddLabel(label) : new SetLabel(label));
    return label;
  }
  return null;
}

function createToLabel(
  human: HumanInterface,
  store: Store,
  labelsService: LabelsService,
  request: LabelsIO.LabelsCreateRequest,
): LabelModel {
  const label = labelsService.makeDefaultStudioLabel();
  label.id = request.labelId || uuid.idWithPrefix('label');
  label.objectId = request.objectId;
  label.pos = request.position;
  label.renderableIndex = 0;
  // set additional API default values not included in makeDefaultStudioLabel
  label.occludable = true;
  label.flyTo = true;
  label.description = '';
  label.shown = true;
  const chapter = store.selectSnapshot(StoryChaptersState.getSelectedChapter);
  label.chapterId = chapter.id;
  return setOptionalParams(label, request, chapter);
}

function updateToLabel(store: Store, request: LabelsIO.LabelsUpdateRequest): LabelModel {
  const label = getLabelById(store, request.labelId);
  const chapter = store.selectSnapshot(StoryChaptersState.getSelectedChapter);
  return setOptionalParams(label, request, chapter);
}

function updateCanvasToLabel(
  store: Store,
  engineService: EngineService,
  request: LabelsIO.Labels2DRequest,
): LabelModel {
  const label = getLabelById(store, request.labelId);
  const canvasPosition = request.canvasPosition;
  const pick: PickModel = {
    x: canvasPosition[0],
    y: canvasPosition[1],
    rayPick: true,
  };
  const hit = engineService.pickScene(pick);
  if (hit && hit.objectId) {
    label.objectId = hit.objectId;
    label.pos = [hit.worldPos[0], hit.worldPos[1], hit.worldPos[2]];
    const tagDistance = label.hideWire ? 20 : 32;
    label.offset = [tagDistance, 0];
    label.barycentric = [hit.barycentric[0], hit.barycentric[1], hit.barycentric[2]];
    label.primitiveIndex = hit.primitiveIndex;
    label.renderableIndex = hit.renderableIndex || 0;
    // label.dir = cameraService.getDirection(hit);
    return label;
  }
  return null;
}

function setLabelOffset(label: LabelModel, offset?: [number, number]): [number, number] {
  if (offset) {
    return offset;
  } else {
    const tagDistance = label.hideWire ? 20 : 32;
    return [-tagDistance, 0];
  }
}

// length check for markup language fields
function checkLength(inputHtml: string, maxLength: number): boolean {
  const span = document.createElement('span');
  span.innerHTML = inputHtml;
  const trimmed = trim(span.textContent);
  return trimmed.length <= maxLength;
}

function setLabelFlyTo(label: LabelModel, flyTo?: boolean): boolean {
  if (flyTo === false) {
    return false;
  }
  return flyTo || label.flyTo;
}

function setLabelStates(label: LabelModel): LabelStateModel[] {
  if (!label.flyTo) {
    return [];
  }
  const states = [new LabelStateModel(), new LabelStateModel()];
  states[0].errands = [new FlyToChapterErrandModel()];
  states[1].errands = label.cameraPosition
    ? [new FlyToCameraErrandModel(label.cameraPosition)]
    : [new FlyToObjectErrandModel(label.objectId)];
}

// set the optional incoming parameters if they are present
function setOptionalParams(
  labelIn: LabelModel,
  request: LabelsIO.LabelsRequest,
  chapter: ChapterModel,
) {
  const label = labelIn;
  label.title = request.title && checkLength(request.title, 100) ? request.title : label.title;
  label.description =
    request.description && checkLength(request.description, 300)
      ? request.description
      : label.description;
  label.theme = request.theme || label.theme;
  label.pinType = request.pinType || label.pinType;
  label.sequentialType = request.sequentialType || label.sequentialType;
  label.offset = setLabelOffset(label, request.labelOffset);
  label.pinGlow = request.pinGlow !== undefined ? request.pinGlow : label.pinGlow;
  label.hideWire = request.hideWire !== undefined ? request.hideWire : label.hideWire;
  label.hideTag = request.hideTag !== undefined ? request.hideTag : label.hideTag;
  label.collapseDescription =
    request.collapseDescription !== undefined
      ? request.collapseDescription
      : label.collapseDescription;
  label.occludable = request.occludable !== undefined ? request.occludable : label.occludable;
  label.cameraPosition = request.flyToCamera || label.cameraPosition;
  label.flyTo = setLabelFlyTo(label, request.flyTo);
  label.states = setLabelStates(label);
  return label;
}

function labelToResponse(human: HumanInterface, label: LabelModel): LabelsIO.LabelsResponse {
  if (label) {
    const marker: MarkerModel = human.scene.markers.markers[label.id];
    return {
      labelId: label.id,
      objectId: label.objectId,
      title: label.title,
      description: label.description,
      theme: label.theme,
      pinType: label.pinType,
      labelOffset: label.offset,
      hideWire: label.hideWire,
      pinGlow: label.pinGlow,
      occludable: label.occludable,
      sequentialType: label.sequentialType,
      shown: label.shown,
      collapseDescription: label.collapseDescription,
      hideTag: label.hideTag,
      flyTo: label.flyTo === true,
      flyToCamera: label.cameraPosition,
      position: marker ? [marker.pos[0], marker.pos[1], marker.pos[2]] : label.pos,
      canvasPosition: marker ? [marker.canvasPos[0], marker.canvasPos[1]] : [-1, -1],
      occluded: marker ? marker.occluded : true,
    };
  }
  return null;
}

function labelsToResponse(
  human: HumanInterface,
  labels: LabelModel[],
): LabelsIO.LabelsInfoResponse {
  return labels.reduce((info, label) => {
    info[label.id] = labelToResponse(human, label);
    return info;
  }, {});
}

type SetLabelEnabledRequest = SetLabelEnabled;
type SetLabelEnabledResponse = SetLabelEnabled;
type SetLabelScaleRequest = SetLabelScaleInterface;
type SetLabelScaleResponse = SetLabelScaleInterface;

type LabelsProcRequest =
  | LabelsIO.LabelsProcRequest
  | AnnotationsIO.AnnotationProcRequest
  | HotspotsIO.HotspotsProcRequest
  | SetLabelEnabledRequest
  | SetLabelScaleRequest;
type LabelsProcResponse =
  | LabelsIO.LabelsProcResponse
  | AnnotationsIO.AnnotationProcResponse
  | HotspotsIO.HotspotsProcResponse
  | SetLabelEnabledResponse
  | SetLabelScaleResponse;
type LabelsEventResponse =
  | LabelsIO.LabelsEventResponse
  | AnnotationsIO.AnnotationEventResponse
  | HotspotsIO.HotspotsEventResponse;

export function getLabelsEvents(
  human: HumanInterface,
  store: Store,
): RPCEvent<LabelsEventResponse>[] {
  return [
    // legacy: annotation events
    ...getAnnotationEvents(human, store),
    // legacy: hotspot events
    ...getHotspotsEvents(human, store),
    // labels events
    {
      eventName: EventsEnum.LabelsChapterInfo,
      mapFn: (labels: LabelModel[]) => labelsToResponse(human, labels),
    },
    {
      eventName: EventsEnum.LabelsCreated,
      mapFn: (label: LabelModel) => labelToResponse(human, label),
    },
    {
      eventName: EventsEnum.LabelsUpdated,
      mapFn: (label: LabelModel) => labelToResponse(human, label),
    },
    {
      eventName: EventsEnum.LabelsDestroyed,
      mapFn: (label: LabelModel) => {
        return { labelId: label.id };
      },
    },
    {
      eventName: EventsEnum.LabelsEntered,
      mapFn: (response: LabelsIO.LabelsIdResponse) => response,
    },
    {
      eventName: EventsEnum.LabelsLeft,
      mapFn: (response: LabelsIO.LabelsIdResponse) => response,
    },
    {
      eventName: EventsEnum.LabelsPicked,
      mapFn: (response: LabelsIO.LabelsIdResponse) => response,
    },
    {
      eventName: EventsEnum.LabelsMoved,
      mapFn: (response: LabelsIO.LabelsMovedResponse) => response,
    },
    {
      eventName: EventsEnum.LabelsEnabled,
      mapFn: () => true,
    },
    {
      eventName: EventsEnum.LabelsDisabled,
      mapFn: () => true,
    },
    {
      eventName: EventsEnum.LabelsShown,
      mapFn: (response: LabelsIO.LabelsIdResponse) => response,
    },
    {
      eventName: EventsEnum.LabelsHidden,
      mapFn: (response: LabelsIO.LabelsIdResponse) => response,
    },
  ];
}

export function getLabelsProcedures(
  human: HumanInterface,
  store: Store,
  labelsService: LabelsService,
  engineService: EngineService,
): Procedure<LabelsProcRequest, LabelsProcResponse>[] {
  return [
    // legacy: annotation procedures
    ...getAnnotationProcedures(human, store, labelsService),
    // legacy: hotspot procedures
    ...getHotspotsProcedures(human, store, labelsService),
    // label procedures
    {
      procName: MessageEnum.LabelsInfo,
      actionFn: (request?: LabelsIO.LabelsInfoRequest) => {
        // optional request for a subset of labels
        if (request.length > 0) {
          return getLabelsById(store, request);
        }
        return store.selectSnapshot(StoryLabelsState.getChapterLabels);
      },
      mapFnOut: (labels: LabelModel[]) => labelsToResponse(human, labels),
    },
    {
      procName: MessageEnum.LabelsCreate,
      mapFnIn: (request: LabelsIO.LabelsCreateRequest) => {
        if (request.labelId && getLabelById(store, request.labelId)) {
          return null; // error if the label id already exists
        }
        if (!request.objectId || !request.position) {
          console.log('attempt to create a label with no objectId and position');
          return null; // is this already handled by required values?
        }
        return createToLabel(human, store, labelsService, request);
      },
      actionFn: (label: LabelModel) => storeLabel(store, label, true),
      mapFnOut: (label: LabelModel) => labelToResponse(human, label),
    },
    {
      procName: MessageEnum.LabelsUpdate,
      mapFnIn: (request: LabelsIO.LabelsUpdateRequest) =>
        getLabelById(store, request.labelId) ? updateToLabel(store, request) : null,
      actionFn: (label: LabelModel) => storeLabel(store, label, false),
      mapFnOut: (label: LabelModel) => labelToResponse(human, label),
    },
    {
      procName: MessageEnum.LabelsCanvasUpdate,
      mapFnIn: (request: LabelsIO.Labels2DRequest) =>
        getLabelById(store, request.labelId)
          ? updateCanvasToLabel(store, engineService, request)
          : null,
      actionFn: (label: LabelModel) => storeLabel(store, label, false),
      mapFnOut: (label: LabelModel) => labelToResponse(human, label),
    },
    {
      procName: MessageEnum.LabelsDestroy,
      mapFnIn: (labelId: LabelsIO.LabelsDestroyRequest) => getLabelById(store, labelId),
      actionFn: (label: LabelModel) => {
        if (label) {
          store.dispatch(new RemoveLabel(label));
          return { labelId: label.id };
        }
        return null; // error if the label doesn't exist
      },
    },
    {
      procName: MessageEnum.LabelsShow,
      actionFn: (request?: LabelsIO.LabelsShowRequest) => {
        if (request.length > 0) {
          store.dispatch(new SetLabelsShown(getLabelsById(store, request), true));
        } else {
          showAllLabels(store, true);
        }
        return true;
      },
    },
    {
      procName: MessageEnum.LabelsHide,
      actionFn: (request?: LabelsIO.LabelsShowRequest) => {
        if (request.length > 0) {
          store.dispatch(new SetLabelsShown(getLabelsById(store, request), false));
        } else {
          showAllLabels(store, false);
        }
        return true;
      },
    },
    {
      procName: MessageEnum.LabelsSetEnabled,
      aliases: [MessageEnum.AnnotationsSetEnabled],
      actionFn: (params: SetLabelEnabledRequest) => {
        store.dispatch(new ToggleLabels(params.enable));
      },
      hideDefinition: true,
    },
    {
      procName: MessageEnum.LabelsSetScale,
      actionFn: (params: SetLabelScaleRequest) => {
        store.dispatch(new SetLabelScale(params.scale));
      },
    },
  ];
}
